import BigNumber from 'bignumber.js'
import { ethers } from 'ethers'
import { useState, useEffect, useCallback } from 'react'
import { useWallet } from '@binance-chain/bsc-use-wallet'
import useRefresh from 'hooks/useRefresh'
import { useERC20 } from 'hooks/useContract'
import { useRanchFactory, useRanch } from 'hooks/useContract2'
import { getBasicTokenInfo, transfer } from 'utils/erc20'
import { getFeeToken, getCreationFee, create, getMyRanches, getRecentRanches, getSingleRanch, transferOwnership, end, deposit, withdraw, setFeeAddress, updateEmissionRate, getFavoriteRanches } from 'utils/ranchUtils'
import { getNullAddress } from 'utils/addressHelpers'

declare const window: any;

export const useFeeToken = () => {
    const [ feeToken, setFeeToken ] = useState({});
    const contract = useRanchFactory()
    const { slowRefresh } = useRefresh();
  
    useEffect(() => {
        const fetch = async () => {
            const feeTokenAddress = await getFeeToken(contract);
            const tokenInfo:any = {};
            await Promise.all([
                getBasicTokenInfo(feeTokenAddress).then((info)=>{
                    Object.assign(tokenInfo, info);
                }),
                getCreationFee(contract).then((fee)=>{
                    Object.assign(tokenInfo, {creationFee: new BigNumber(fee) });
                })
            ]);
            tokenInfo.creationFee = tokenInfo.creationFee.div(new BigNumber(10).pow(tokenInfo.decimals)).toNumber();
            setFeeToken(tokenInfo);
        }
  
        if (contract) fetch()
    }, [slowRefresh, contract])
  
    return feeToken;
}

export const useCreateRanch = () => {
    const { account } = useWallet()
    const contract = useRanchFactory()

    const handleCreate = useCallback(
        async (token: any, lpTokenAddress: any, emissionPerBlock: BigNumber, startBlock:any, depositFeeBP:any, amount: BigNumber) => {
            try {
                const adjustedAmount = amount.times(new BigNumber(10).pow(token.decimals)).toFixed(0);
                const adjustedEmissionPerBlock = emissionPerBlock.times(new BigNumber(10).pow(token.decimals)).toFixed(0);
                const txHash = await create(contract, token.address, lpTokenAddress, adjustedEmissionPerBlock, startBlock, depositFeeBP, adjustedAmount, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleCreate;
}

export const useMyRanches = (currentPage) => {
    const [ ranches, setMyRanches ] = useState({
        currentPage: 1,
        pageCount: 0,
        ranches: [],
        isLoading: true
    });
    const { account } = useWallet();
    const contract = useRanchFactory()
    const { fastRefresh } = useRefresh();

    useEffect(() => {
        const fetch = async () => {
            let myRanches: any = false;
            if (account) {
                myRanches = await getMyRanches(contract, account, currentPage);
            } else {
                myRanches = {
                    currentPage: 1,
                    pageCount: 0,
                    ranches: [],
                    isLoading: true
                };
            }
            if (myRanches !== false) setMyRanches(myRanches);
        }
  
        if (contract) fetch()
    }, [fastRefresh, contract, account, currentPage])
  
    return ranches;
}

export const useRecentRanches = (currentPage) => {
    const [ ranches, setRanches ] = useState({
        currentPage: 1,
        pageCount: 0,
        count: 0,
        ranches: [],
        isLoading: true
    });
    const { account } = useWallet();
    const contract = useRanchFactory()
    const { fastRefresh } = useRefresh();

    useEffect(() => {
        const fetch = async () => {
            const recentRanches = await getRecentRanches(contract, account, currentPage);
            if (recentRanches !== false) setRanches(recentRanches);
        }
  
        if (contract) fetch()
    }, [fastRefresh, contract, account, currentPage])
  
    return ranches;
}

export const useSingleRanch = (ranchAddress) => {
    const [ ranch, setRanches ] = useState([]);
    const { account } = useWallet();
    const contract = useRanchFactory()
    const { fastRefresh } = useRefresh();

    useEffect(() => {
        const fetch = async () => {
            const singleRanchArray = await getSingleRanch(contract, ethers.utils.getAddress(ranchAddress), account);
            if (singleRanchArray !== false) setRanches(singleRanchArray);
        }
  
        if (contract && ranchAddress) fetch()
    }, [fastRefresh, contract, ranchAddress, account])
  
    return ranch;
}

export const useTransferOwnership = (ranchAddress) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleTransferOwnership = useCallback(
        async (newOwner: any) => {
            try {
                const txHash =  await transferOwnership(contract, newOwner, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleTransferOwnership;
}

export const useRenounce = (ranchAddress) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleRenounce = useCallback(
        async () => {
            try {
                const txHash =  await transferOwnership(contract, getNullAddress(), account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleRenounce;
}

export const useEnd = (ranchAddress) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleConclude = useCallback(
        async () => {
            try {
                const txHash = await end(contract, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleConclude;
}

export const useStake = (ranchAddress) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleAction = useCallback(
        async (amount: any) => {
            try {
                const txHash = await deposit(contract, new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed(0), account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleAction;
}

export const useUnstake = (ranchAddress) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleAction = useCallback(
        async (amount: any) => {
            try {
                const txHash = await withdraw(contract, new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed(0), account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleAction;
}

export const useHarvest = (ranchAddress) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleAction = useCallback(
        async () => {
            try {
                const txHash = await deposit(contract, '0', account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleAction;
}

export const useInject = (ranchAddress, token) => {
    const { account } = useWallet()
    const contract = useERC20(token.address)

    const handleBurn = useCallback(
        async (amount: BigNumber) => {
            try {
                const txHash = await transfer(contract, ranchAddress, amount.times(new BigNumber(10).pow(token.decimals)).toString(), account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract, token, ranchAddress],
    )

    return handleBurn;
}

export const useSetFeeAddress = (ranchAddress) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleAction = useCallback(
        async (feeAddress: any) => {
            try {
                const txHash =  await setFeeAddress(contract, feeAddress, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract],
    )

    return handleAction;
}

export const useUpdateEmissionRate = (ranchAddress, token) => {
    const { account } = useWallet()
    const contract = useRanch(ranchAddress)

    const handleAction = useCallback(
        async (emissionPerBlock: BigNumber) => {
            try {
                const adjustedEmissionPerBlock = emissionPerBlock.times(new BigNumber(10).pow(token.decimals)).toFixed(0);
                const txHash =  await updateEmissionRate(contract, adjustedEmissionPerBlock, account);
                return txHash
            } catch (e) {
                return false
            }
        },
        [account, contract, token],
    )

    return handleAction;
}

export const useFavoriteRanches = (currentPage) => {
    const [ ranches, setRanches ] = useState({
        currentPage: 1,
        pageCount: 0,
        ranches: [],
        isLoading: true
    });
    const { account } = useWallet();
    const contract = useRanchFactory()
    const { fastRefresh } = useRefresh();

    useEffect(() => {
        const fetch = async () => {
            const favoriteRanches = await getFavoriteRanches(contract, account, currentPage);
            if (favoriteRanches !== false) setRanches(favoriteRanches);
        }
  
        if (contract) fetch()
    }, [fastRefresh, contract, account, currentPage])
  
    return ranches;
}
